import React, { useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  Option,
  SearchBox,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableBody,
  TableCell,
  TableCellLayout,
  Spinner,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text
} from '@fluentui/react-components';
import { getPlacementDocuments, getSupplierPlacementDocuments } from '../services/placementService';
import { AddCircleFilled, ArrowLeftRegular, ArrowRightRegular, ChevronDownRegular, DismissRegular } from '@fluentui/react-icons';
import { useAppContext } from '../AppContext';
import { Module } from '../models/module';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Paginator from '../utils/Paginator';
import CreatePlacement from './Placement/PlacementDialogs/CreatePlacement';
import Tendering from './Tendering';
import { Organization } from '../models/organizations';
import { PlacementDoc } from '../models/placements';
import { getTenderingDocument } from '../services/tenderingService';
import { RoleDTO } from '../models/role';
import { getOrganizations } from '../services/organizationService';
import { getUserGroups, getUserGroupsByUser } from '../services/userGroupService';

const PlacementsComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {user} = useAppContext();

  const [placements, setPlacements] = useState<any[]>([]);
  const [selectedModule, setSelectedModule] = useState<Module | null>(() => {
    const moduleID = searchParams.get('module');
    if (moduleID) {
      return user.modules.find((m: Module) => m.moduleID.toString() === moduleID) ?? null;
    }
    return null;
  });
  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(() => searchParams.get('org') ?? null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [page, setPage] = useState<number>(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 0;
  });
  const [placementCount, setPlacementCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [tenderingModules, setTenderingModules] = useState<Module[]>([]);
  const [tenderModules, setTenderModules] = useState<Module[]>([]);
  const [selectedTenderingDocument, setSelectedTenderingDocument] = useState<any>(null);
  const [showCreatePlacement, setShowCreatePlacement] = useState<boolean>(false);
  // A user whose only role is Behörighetsansvarig can't own placements themselves (roleType
  // 'Procurer' is shared by all municipality-side roles), so they must always view group placements.
  const isOnlyBehorighetsansvarig = user.roles.length > 0 && user.roles.every((r: RoleDTO) => r.roleName === 'Behörighetsansvarig');
  const [getAdminPlacements, setGetAdminPlacements] = useState<boolean>(() => isOnlyBehorighetsansvarig ? true : searchParams.get('admin') === 'true');
  const [searchQuery, setSearchQuery] = useState<string>(() => searchParams.get('search') ?? '');
  const [committedSearch, setCommittedSearch] = useState<string>(() => searchParams.get('search') ?? '');
  const [userGroups, setUserGroups] = useState<any[]>([]);

  const { featurePermissions, getFeaturePermissionsByModuleID } = useAppContext();

  const navigate = useNavigate();

  useEffect(() => {
    if (user.roles.some((r: RoleDTO) => r.roleType === 'General')) {
      getOrganizations().then((orgs: Organization[]) => {
        setOrganizations(orgs.filter((o: Organization) => o.organizationTypeID === 2));
      }).catch((err: any) => console.error('Error fetching organizations:', err));
    }
  }, []);

  useEffect(() => {
    if (featurePermissions?.some((fp: any) => fp.action === 'See extended placement overview columns')) {
      const fetchUserGroups = user.roles.some((r: RoleDTO) => r.roleType === 'General')
        ? getUserGroups()
        : getUserGroupsByUser(user.user.userID);
      fetchUserGroups.then((groups: any[]) => setUserGroups(groups))
        .catch((err: any) => console.error('Error fetching user groups:', err));
    }
  }, [featurePermissions]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (page > 0) params.page = page.toString();
    if (selectedModule) params.module = selectedModule.moduleID.toString();
    if (getAdminPlacements) params.admin = 'true';
    if (committedSearch) params.search = committedSearch;
    if (selectedOrganization) params.org = selectedOrganization;
    setSearchParams(params, { replace: true });
  }, [page, selectedModule, getAdminPlacements, committedSearch, selectedOrganization]);
  
  useEffect(() => {
    setTenderModules(user.modules.filter((module: Module) => module.moduleType === 'Tender'));
    let moduleIDs: number[] = [];
    let organizationNames: string[] = [];
    if(user.roles.some((role: any) => role.roleType === 'Procurer')){
      organizationNames = user.organization.organizationName;
    }
    if(user.roles.some((role: any) => role.roleType === 'Supplier')){
      moduleIDs = user.modules.filter((module: Module) => module.moduleType === 'Tendering').map((module: Module) => module.moduleID);
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        if(user.roles.some((role: any) => role.roleType === 'Supplier') && !user.organization){
          const tenderIDs = featurePermissions?.filter((fp: any) => fp.action === 'Accept or deny placement request' || fp.action === 'Accept or deny placement request through organization mandate').map((fp: any) => fp.moduleID) || [];
          const placementResult = await getSupplierPlacementDocuments(tenderIDs, page);

          setPlacements(placementResult.placementDocuments);
          setPlacementCount(placements.length);
        }
        else{
          const placementResult = await getPlacementDocuments(
            selectedOrganization ?? [],
            selectedModule ? selectedModule.moduleID : moduleIDs,
            getAdminPlacements ? getUserAdminGroups() : [],
            getAdminPlacements ? undefined : user.roles.some((r: RoleDTO) => r.roleType === 'Procurer') ? user.user.userID : undefined,
            page,
            committedSearch || undefined
          );
          setPlacements(placementResult.placementDocuments);
          setPlacementCount(placementResult.count);
        }
      } catch (error) {
        console.error('Error fetching placements:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [selectedModule, page, getAdminPlacements, committedSearch, selectedOrganization]);

  useEffect(() => {
    // General/Procurer users get all Tendering modules from the backend regardless of user group membership -
    // only restrict by userGroupAssignments for other roles who create placements through a specific group.
    const isGeneralOrProcurer = user.roles.some((r: RoleDTO) => r.roleType === 'General' || r.roleType === 'Procurer');
    setTenderingModules(user.modules.filter((module: Module) => module.moduleType === 'Tendering'
      && (isGeneralOrProcurer || user.userGroupAssignments.some((uga: any) => uga.userGroup.moduleID === module.moduleID))));
  }, [placements])

  const hasExtendedPlacementColumns = featurePermissions?.some((fp: any) => fp.action === 'See extended placement overview columns');

  const getColumnWidthClass = (key: string, index: number): string => {
    if (key === "creatorEmail") return "w-[22%]";
    if (key === "reference") return "w-[8%]";
    if (key === "status") return "w-[8%]";
    return index === 0 ? "w-1/4" : "w-auto";
  };

  const columns = [
    { key: 'name', name: 'Namn' },
    ...(hasExtendedPlacementColumns ? [{ key: 'tendering', name: 'Ramavtal' }] : []),
    ...(user.roles.some((r: RoleDTO) => r.roleType === 'General') ? [{ key: 'user', name: 'Användare' }] : []),
    ...(hasExtendedPlacementColumns ? [{ key: 'creatorEmail', name: 'E-post' }] : []),
    { key: 'reference', name: 'Referens' },
    ...(!user.roles.some((r: RoleDTO) => r.roleType === 'Supplier') ? [{ key: 'status', name: 'Status' }] : []),
    ...(user.roles.some((r: RoleDTO) => r.roleType === 'Supplier') ? [{ key: 'answerDate', name: 'Svarsdatum' }] : []),
    ...(user.roles.some((r: RoleDTO) => r.roleType === 'Supplier') ? [{ key: 'isPlacementRequestAccepted', name: 'Svar' }] : []),
    ...(hasExtendedPlacementColumns ? [
      { key: 'userGroupName', name: 'Gruppnamn' },
      { key: 'requestDate', name: 'Datum för förfrågan' },
    ] : []),
  ];


  const status = {
    search: 'Utkast',
    sent: 'Skickad',
    accepted: 'Verksamhet vald',
    cancelled: 'Avbruten',
  };

  const handleRowClick = (placementDocumentID: string, tenderID?: string) => {
    if(user.roles.some((role: any) => role.roleType === 'Supplier') && !user.organization){
      navigate(`/forfragan?placementID=${placementDocumentID}&tenderID=${tenderID}`);
    }
    else{
      navigate(`/forfragningar/${placementDocumentID}`);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  }

  const getUserAdminGroups = (): number[] => {
    return user.userGroupAssignments
      .filter((uga: any) => uga.userGroup.userGroupAssignments?.some((member: any) =>
        member.user?.userID === user.user.userID && member.roles?.some((r: any) => r.roleName === "Behörighetsansvarig")
      ))
      .map((uga: any) => uga.userGroup.userGroupID);
  }

  // "Egna förfrågningar" only exist for procurers (getPlacementDocuments only filters by userID for that role),
  // so a Behörighetsansvarig without any other procurer role has no personal placements to switch back to.
  const hasOwnPlacements = user.roles.some((r: RoleDTO) => r.roleType === 'Procurer') && !isOnlyBehorighetsansvarig;

  const getTenderModulesByTendering = (moduleID: number): Module[] => {
    return tenderModules.filter(tm => +tm.modulePath.split('_')[2] === moduleID).sort((a, b) => a.moduleName.localeCompare(b.moduleName));
  }

  const getTenderName = (tenderID: number): string => {
    const module = user.modules?.find((m: Module) => m.moduleID === tenderID);
    return module?.moduleName ?? '';
  };

  const getUserGroupName = (userGroupID: number): string => {
    const group = userGroups.find((ug: any) => ug.userGroupID === userGroupID);
    return group?.userGroupName ?? '';
  };

  // content.user is either a single User (creator only) or a User[] once the
  // placement has been shared with more users - normalize to an array so the
  // table doesn't silently go blank for shared placements.
  const getPlacementUsers = (item: any): any[] => {
    if (!item.content.user) return [];
    return Array.isArray(item.content.user) ? item.content.user : [item.content.user];
  };

  const normalizeSearchQuery = (value: string): string => {
    const trimmed = value.trim();
    // Convert YYMMDD (e.g. 260327) → YYYY-MM-DD (e.g. 2026-03-27)
    const shortDate = /^(\d{2})(\d{2})(\d{2})$/;
    const shortMatch = trimmed.match(shortDate);
    if (shortMatch) {
      return `20${shortMatch[1]}-${shortMatch[2]}-${shortMatch[3]}`;
    }
    // Convert YYYYMMDD (e.g. 20260327) → YYYY-MM-DD (e.g. 2026-03-27)
    const longDate = /^(\d{4})(\d{2})(\d{2})$/;
    const longMatch = trimmed.match(longDate);
    if (longMatch) {
      return `${longMatch[1]}-${longMatch[2]}-${longMatch[3]}`;
    }
    return value;
  };

  const handleCreatePlacement = async (moduleID: number) => {
    try {
      const tenderingDoc = await getTenderingDocument(moduleID, ["content"]);
      setSelectedTenderingDocument(tenderingDoc);
      setShowCreatePlacement(true);
    } catch (error) {
      console.error('Error fetching tendering document:', error);
    }
  }

  return (
    <>
    <div className="flex justify-center bg-[#ebebeb] min-h-screen">
     <div className="flex flex-col w-full items-center h-36 p-4 bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b mx-auto">
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-md w-full max-w-[1480px] mt-4">
        <div className="flex justify-between items-center mb-4 w-full">
          <h1 className="text-2xl font-bold">Förfrågningar</h1>
            {featurePermissions?.some(fp => fp.action === 'Create placement') && (
          <Menu>
            <MenuTrigger>
              <Button icon={<AddCircleFilled />} appearance="primary" className="ml-auto" disabled={loading}>
                Ny förfrågan <ChevronDownRegular />
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {tenderingModules
                  .filter(module => 
                    !module.isHidden &&
                    getFeaturePermissionsByModuleID(module.moduleID)?.some(fp => fp.action === 'Create placement')
                  )
                  .map((module) => (
                    <MenuItem key={module.moduleID} onClick={() => handleCreatePlacement(module.moduleID)}>
                      {module.moduleName}
                    </MenuItem>
                  ))}
              </MenuList>
            </MenuPopover>
          </Menu>)}
        </div>
        {(user.roles.some((role: any) => role.roleType === 'General') || user.roles.some((role: any) => role.roleType === 'Procurer')) && <div className="flex gap-2 flex-wrap mb-4">
          <div className="flex items-center gap-1">
            <Dropdown
              placeholder="Välj avtal"
              clearable
              value={selectedModule?.moduleName || ""}
              disabled={loading}
              positioning={{autoSize: false}}
              listbox={{
                style: {
                  maxHeight: '600px',
                  overflowY: 'auto'
                },
              }}
              onOptionSelect={(_, data) => {
                const module = tenderingModules.find(m => m.moduleID.toString() === data.optionValue);
                setSelectedModule(module || null);
                setPage(0); // Reset page when changing module
              }}
            >
              {tenderingModules.map((module) => (
                <Option key={module.moduleID} value={module.moduleID.toString()}>
                  {module.moduleName}
                </Option>
              ))}
            </Dropdown>
            {user.roles.some((role: any) => role.roleType === 'General') && (
              <Dropdown
                placeholder="Välj organisation"
                clearable
                value={selectedOrganization || ""}
                disabled={loading}
                positioning={{autoSize: false}}
                listbox={{
                  style: {
                    maxHeight: '600px',
                    overflowY: 'auto'
                  },
                }}
                onOptionSelect={(_, data) => {
                  setSelectedOrganization(data.optionValue ?? null);
                  setPage(0);
                }}
              >
                {organizations.map((org) => (
                  <Option key={org.organizationID} value={org.organizationName}>
                    {org.organizationName}
                  </Option>
                ))}
              </Dropdown>
            )}
            {getUserAdminGroups().length > 0 && hasOwnPlacements &&
              <Button onClick={() => setGetAdminPlacements(!getAdminPlacements)}>
                {getAdminPlacements ? 'Visa egna förfrågningar' : 'Administrera gruppförfrågningar'}
              </Button>
            }
          </div>
        </div>}

        <div className="flex gap-2 items-center mb-4 w-full">
          <SearchBox
            placeholder="Sök förfrågan..."
            value={searchQuery}
            onChange={(_, data) => {
              setSearchQuery(data.value);
              if (!data.value) {
                setPage(0);
                setCommittedSearch('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                setCommittedSearch(normalizeSearchQuery(searchQuery));
              }
            }}
            disabled={loading}
            style={{ flex: 1 }}
          />
          <Button
            appearance="secondary"
            disabled={loading}
            onClick={() => {
              setPage(0);
              setCommittedSearch(normalizeSearchQuery(searchQuery));
            }}
          >
            Sök
          </Button>
        </div>
        <Text size={200} className="block -mt-3 mb-4 pl-2.5 text-gray-500">
          Sökningen görs på namn, referens, ramavtal, gruppnamn, e-post och datum
        </Text>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner label="Laddar förfrågningar..." />
          </div>
        ) : (
          <Table aria-label="Förfrågningar tabell" className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                {columns.map((col, index) => (
                  <TableHeaderCell key={col.key} className={`truncate max-w-0 ${getColumnWidthClass(col.key, index)}`}>
                    <Text weight="semibold" truncate wrap={false} title={col.name}>
                      {col.name}
                    </Text>
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {placements.map((item) => (
                <TableRow 
                  key={item.placementDocumentID}
                  onClick={() => handleRowClick(item.placementDocumentID, item.content.tenderID ? item.content.tenderID : undefined)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  {columns.map((col, index) => {
                    const creatorEmails = col.key === "creatorEmail" ? getPlacementUsers(item).map(u => u.email) : [];
                    const value = col.name === "Status"
                      ? status[item.content[col.key] as keyof typeof status]
                      : col.key === "name"
                        ? `${item.content[col.key]}${user.roles.some((r: any) => r.roleType === 'Supplier') && item.content.tenderID ? ` — ${getTenderName(item.content.tenderID)}` : ''}`
                        : col.key === "user"
                          ? getPlacementUsers(item).map(u => `${u.firstName} ${u.lastName}`).join(', ')
                          : col.key === "answerDate"
                            ? item.content.answerDate ? new Date(item.content.answerDate).toLocaleDateString('sv-SE') : ''
                            : col.key === "isPlacementRequestAccepted"
                              ? item.content.isPlacementRequestAccepted === true ? 'Accepterad' : item.content.isPlacementRequestAccepted === false ? 'Nekad' : 'Ej besvarad'
                              : col.key === "tendering"
                                ? item.content.tenderingName ?? ''
                                : col.key === "userGroupName"
                                  ? getUserGroupName(item.content.userGroupID)
                                  : col.key === "requestDate"
                                    ? item.content.logs?.created ? new Date(item.content.logs.created).toLocaleDateString('sv-SE') : ''
                                    : col.key === "creatorEmail"
                                      ? creatorEmails.join(', ')
                                      : item.content[col.key];
                    return (
                      <TableCell key={col.key} className={col.key === "creatorEmail" ? `whitespace-normal break-all ${getColumnWidthClass(col.key, index)}` : `truncate max-w-0 ${getColumnWidthClass(col.key, index)}`}>
                        {col.key === "creatorEmail" ? (
                          <TableCellLayout>
                            <div className="flex flex-col">
                              {creatorEmails.map((email, i) => <span key={i}>{email}</span>)}
                            </div>
                          </TableCellLayout>
                        ) : (
                          <TableCellLayout truncate title={value ?? ''}>
                            {value}
                          </TableCellLayout>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!loading && (
          <Paginator currentPage={page} totalCount={placementCount} onPageChange={handlePageChange}/>
        )}
      </div>
     </div>
    </div>
    <CreatePlacement 
      open={showCreatePlacement} 
      onClose={() => setShowCreatePlacement(false)} 
      tenderingDocument={selectedTenderingDocument}
    />
    </>
  );
};

export default PlacementsComponent;